import React, { SyntheticEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Z_INDEX } from '../../styles/variables';
import { motion } from 'framer-motion';
import { flexCenterAll, setIconFillColor } from '../../styles/mixins';
import { stopEventPropagation } from '../../utils/shared.utils';
import { Close24, ArrowLeft24, ArrowRight24 } from '@honeybook/icons';
import { vars } from '../../theme';
import { applyOpacity } from '../../utils/color.utils';
import HBMediaElement from '../HBMediaElement/HBMediaElement';
import { LayoutBreakpoints } from '../common/LayoutBreakpoints/LayoutBreakpoints';
import { useLockBodyScroll } from 'react-use';
import HBIconButton from '../HBIconButton/HBIconButton';

const TOP_BOTTOM_PADDING = 52;

export interface HBLightBoxProps {
    /** Array of image URLs to display in the lightbox; renders navigation arrows when more than one URL is provided. */
    imageUrls: string[];
    onClose: () => void;
}

const HBLightBox = ({ imageUrls, onClose }: HBLightBoxProps) => {
    const [isShown, setIsShown] = useState(true);
    const [imageIndex, setImageIndex] = useState(0);

    useLockBodyScroll(isShown);

    const onAnimationComplete = () => {
        if (!isShown) {
            onClose();
        }
    };

    useEffect(() => {
        const onKeyUp = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                ev.stopPropagation();
                setIsShown(false);
            }
        };
        if (isShown) {
            document.addEventListener('keyup', onKeyUp, true);
            return () => document.removeEventListener('keyup', onKeyUp, true);
        }
    }, [isShown]);

    const Arrows = () => {
        const onLeftArrowClick = (e: SyntheticEvent) => {
            e.stopPropagation();
            if (imageIndex === 0) {
                setImageIndex(imageUrls.length - 1);
            } else {
                setImageIndex(imageIndex - 1);
            }
        };

        const onRightArrowClick = (e: SyntheticEvent) => {
            e.stopPropagation();
            if (imageIndex === imageUrls.length - 1) {
                setImageIndex(0);
            } else {
                setImageIndex(imageIndex + 1);
            }
        };

        return (
            <>
                <LeftArrow
                    buttonTheme="subtle-invisible"
                    icon={ArrowLeft24}
                    label="left"
                    onClick={onLeftArrowClick}
                    size="sm"
                    testIdName="left"
                />
                <RightArrow
                    buttonTheme="subtle-invisible"
                    icon={ArrowRight24}
                    label="right"
                    onClick={onRightArrowClick}
                    size="sm"
                    testIdName="right"
                />
            </>
        );
    };

    return (
        <Backdrop
            initial={{ opacity: 0 }}
            animate={isShown ? { opacity: 1 } : { opacity: 0 }}
            transition={transition}
            onAnimationComplete={onAnimationComplete}
            onClick={() => setIsShown(false)}
        >
            <StyledLayoutBreakpoints>
                <HBMediaElement path={imageUrls[imageIndex]} isFromLightbox />
            </StyledLayoutBreakpoints>

            <CloseIcon
                buttonTheme="subtle-invisible"
                icon={Close24}
                label="close"
                onClick={() => setIsShown(false)}
                size="sm"
                testIdName="close"
            />

            {imageUrls.length > 1 && <Arrows />}
        </Backdrop>
    );
};

export default HBLightBox;

// -------------------------------- Styles -------------------------------- //
const transition = { duration: 0.3, type: 'tween', ease: 'easeInOut' };

const Backdrop = styled(motion.div)`
    position: fixed;
    z-index: ${Z_INDEX.POPOVER};
    inset: 0;

    ${flexCenterAll};
    justify-content: center;
    align-items: center;
    padding: 2rem;

    background-color: ${applyOpacity(
        vars.colors.surface.containerInverted,
        80
    )};
    cursor: pointer;
`;

const CloseIcon = styled(HBIconButton)`
    position: absolute;
    top: 16px;
    right: 16px;
    ${setIconFillColor(vars.colors.text.invertedPrimary)};
`;

const LeftArrow = styled(HBIconButton)`
    position: absolute;
    left: 25px;
    ${setIconFillColor(vars.colors.text.invertedPrimary)};
`;

const RightArrow = styled(HBIconButton)`
    position: absolute;
    right: 25px;
    ${setIconFillColor(vars.colors.text.invertedPrimary)};
`;

const StyledLayoutBreakpoints = styled(LayoutBreakpoints).attrs({
    onClick: stopEventPropagation
})`
    margin: 0;
    cursor: auto;
    img {
        max-height: calc(100vh - ${TOP_BOTTOM_PADDING}px);
    }
`;
